import { create } from 'zustand';
import type { RobotState } from '@fabricos/protocol-ts';

/** Capacity of the per-robot ring buffer. 5 Hz × 600 s = 3000 frames. */
const RING_CAP = 3000;
/** Max number of distinct robots we keep buffers for — LRU evicts beyond. */
const MAX_ROBOTS = 8;
/** Hard cap on total frames across all buffers. ~250 B/frame × 24 k = ~6 MB. */
const MAX_TOTAL_FRAMES = 24_000;

interface RingBuffer {
  /** Circular buffer of frames newest-last. */
  frames: RobotState[];
  /** Earliest ts_ms in the buffer. */
  startTs: bigint | null;
  /** Latest ts_ms in the buffer. */
  endTs: bigint | null;
  /** Wall-clock of the last capture — used for LRU. */
  lastTouched: number;
}

interface ReplayStore {
  /** Per-robot ring buffer. */
  buffers: Record<string, RingBuffer>;

  /** Playback state. */
  active: boolean;
  robotId: string | null;
  /** Position within the buffer in ms relative to startTs. */
  positionMs: number;
  speed: 1 | 2 | 4 | 8;

  capture: (state: RobotState) => void;
  clear: (robotId?: string) => void;

  start: (robotId: string) => void;
  stop: () => void;
  togglePlay: () => void;
  setPosition: (ms: number) => void;
  setSpeed: (s: 1 | 2 | 4 | 8) => void;

  /** Get the frame closest to (and not after) the current position. */
  currentFrame: (robotId?: string) => RobotState | null;
  /** Duration of the buffer (ms). */
  durationMs: (robotId?: string) => number;
}

export const useReplay = create<ReplayStore>((set, get) => ({
  buffers: {},
  active: false,
  robotId: null,
  positionMs: 0,
  speed: 1,

  capture: (state) =>
    set((s) => {
      const now = Date.now();
      const ring = s.buffers[state.robot_id] ?? {
        frames: [],
        startTs: null,
        endTs: null,
        lastTouched: now,
      };
      const frames = [...ring.frames, state];
      if (frames.length > RING_CAP) frames.splice(0, frames.length - RING_CAP);
      const updated: Record<string, RingBuffer> = {
        ...s.buffers,
        [state.robot_id]: {
          frames,
          startTs: frames[0]?.ts_ms ?? null,
          endTs: frames[frames.length - 1]?.ts_ms ?? null,
          lastTouched: now,
        },
      };
      // Apply LRU: evict oldest robots beyond MAX_ROBOTS, and beyond MAX_TOTAL_FRAMES.
      let entries = Object.entries(updated);
      if (entries.length > MAX_ROBOTS) {
        entries.sort((a, b) => a[1].lastTouched - b[1].lastTouched);
        entries = entries.slice(entries.length - MAX_ROBOTS);
      }
      let total = entries.reduce((n, [, r]) => n + r.frames.length, 0);
      while (total > MAX_TOTAL_FRAMES && entries.length > 1) {
        entries.sort((a, b) => a[1].lastTouched - b[1].lastTouched);
        const [, evicted] = entries.shift()!;
        total -= evicted.frames.length;
      }
      return { buffers: Object.fromEntries(entries) };
    }),

  clear: (robotId) =>
    set((s) => {
      if (!robotId) return { buffers: {} };
      const next = { ...s.buffers };
      delete next[robotId];
      return { buffers: next };
    }),

  start: (robotId) => set({ active: true, robotId, positionMs: 0 }),
  stop: () => set({ active: false }),
  togglePlay: () => set((s) => ({ active: !s.active })),
  setPosition: (positionMs) => set({ positionMs }),
  setSpeed: (speed) => set({ speed }),

  currentFrame: (robotId) => {
    const s = get();
    const id = robotId ?? s.robotId;
    if (!id) return null;
    const ring = s.buffers[id];
    if (!ring || ring.frames.length === 0 || !ring.startTs) return null;
    const targetTs = Number(ring.startTs) + s.positionMs;
    // Binary-search-ish: linear is fine for 3000 frames.
    let pick = ring.frames[0]!;
    for (const f of ring.frames) {
      if (Number(f.ts_ms) <= targetTs) pick = f;
      else break;
    }
    return pick;
  },

  durationMs: (robotId) => {
    const s = get();
    const id = robotId ?? s.robotId;
    if (!id) return 0;
    const ring = s.buffers[id];
    if (!ring || !ring.startTs || !ring.endTs) return 0;
    return Number(ring.endTs) - Number(ring.startTs);
  },
}));
